import React, {ReactElement, useCallback} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {observer} from 'mobx-react-lite';
import cx from 'classnames';
import {
    Button,
    defaultDropDownV2Context,
    DropDownV2Context,
    Draggable,
    DropdownV2,
    Flex,
    Icon,
    PriorityIcon,
    Scrollbar,
    Title,
    Link,
    Text
} from '@priority-software/priority-style-react';
import {DraggableList} from '../../../../../../../components/commonOldtoNew';
import SavedSearchItem from './parts/SavedSearchItem';
import {DropdownBaseItem} from './parts/DropdownItem';
import {texts} from '../../texts';
import {useKeyboardNavigation} from '../../../../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {SavedSearch} from '../../../../../types';
import {isRTL} from '../../../../../../../Localization';
import {mapSearches} from './utils';
import {showFormClientSideMessage} from '../../../../../actions/messages';
import styles from './styles.module.scss';

type Props = {
    deleteSearch: (id: string) => void;
    toggleDefault: (id: string) => void;
    onReorder: (searches: SavedSearch[]) => void;
    executeSearch: (search: SavedSearch) => void;
    onEditSearch?: (id: string) => void;
    onClose: (event?: any) => void;
    onOpen: (event?: any) => void;
    openNewSearch?: () => void;
    openCurrentSearch?: () => void;
    portalSelector?: string;
    searches: SavedSearch[];
    componentClasses?: string;
    contentClasses?: string;
    trigger: ReactElement;
    isOpen: boolean;
    recentSearches?: SavedSearch[];
    usePortal?: boolean;
    withShortcut?: boolean;
    currentSearchId?: string | null;
    filterCount: number;
    hideUnsavedSearches?: boolean;
};

export const SavedSearches = observer(
    ({
        searches,
        componentClasses,
        recentSearches,
        contentClasses,
        trigger,
        isOpen,
        onOpen,
        usePortal,
        portalSelector,
        withShortcut = true,
        onClose,
        onReorder,
        openNewSearch,
        openCurrentSearch,
        executeSearch,
        deleteSearch,
        onEditSearch,
        toggleDefault,
        currentSearchId,
        hideUnsavedSearches,
        filterCount
    }: Props) => {
        const showUnsavedSearchItem = currentSearchId
            ? searches.findIndex(s => s.id === currentSearchId) === -1
            : filterCount > 0;

        const onSelect = (id: string) => {
            const search = searches.find(s => s.id === id) || recentSearches.find(s => s.id === id);
            if (search) {
                executeSearch(search);
            }
            onClose();
        };

        const onSearchRemove = useCallback(
            (id: string) => {
                showFormClientSideMessage('', priority.netitems.shared.Message.Type.Warning, {
                    content: texts.deleteSearchWarning,
                    onConfirm: () => deleteSearch(id),
                    cancleButtonText: texts.noLabel
                });
            },
            [deleteSearch]
        );

        const preparedSearches = mapSearches(searches);

        const searchesCount = preparedSearches.length + +showUnsavedSearchItem;

        const handleReorder = (orderedSearches: SavedSearch[]) => {
            const oldIndexes = preparedSearches.map(search => search.index);

            const data = orderedSearches.map((search, newIndex) => {
                return {
                    ...search,
                    index: oldIndexes[newIndex] // Assign the correct sequential index from oldIndexes
                };
            });

            onReorder(data);
        };

        const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
            initialFocusedIndex: -1,
            listLength: searchesCount,
            pageSize: 1,
            onSelect: () => onSelect(searches[focusedIndex]?.id)
        });

        return (
            <DropDownV2Context.Provider
                value={{
                    ...defaultDropDownV2Context,
                    isOpen,
                    closeDropdown: onClose,
                    openDropdown: onOpen
                }}
            >
                <DropdownV2
                    onKeyDown={handleKeyboardNavigation}
                    componentClasses={cx(styles.content, componentClasses)}
                    contentComponentClasses={cx(styles.list, contentClasses)}
                    isOpen={isOpen}
                    onClose={onClose}
                    trigger={trigger}
                    usePortal={usePortal}
                    portalSelector={portalSelector}
                >
                    <Flex direction="column" alignItems={'flex-start'} style={{width: 480}}>
                        {showUnsavedSearchItem && !hideUnsavedSearches ? (
                            <DropdownBaseItem
                                isFocused={focusedIndex === 0}
                                componentClasses={styles.unSavedItem}
                                onClick={openCurrentSearch}
                            >
                                <Flex>
                                    <Text skin="paragraph-2" componentClasses={styles.text}>
                                        {texts.newSearch}
                                    </Text>

                                    <Link className={styles.saveLabel}>{texts.save}</Link>
                                </Flex>
                            </DropdownBaseItem>
                        ) : null}

                        {preparedSearches.length > 0 ? (
                            <>
                                <div className={cx(styles.container, styles.title)}>
                                    <Title
                                        skin="h6"
                                        fontWeight="medium"
                                        text={texts.savedSearches}
                                    />
                                </div>

                                <Scrollbar
                                    rtl={isRTL()}
                                    autoHeightMax={165}
                                    autoHeight
                                    componentClasses={styles.scroll}
                                >
                                    <DraggableList
                                        handleDNDFinish={handleReorder}
                                        componentClasses={styles.list}
                                        gradientScroll
                                        autoHeight
                                        autoHeightMax={165}
                                        data={preparedSearches}
                                        renderItem={({item, index, ...draggableProps}) => {
                                            const currentIndex: number =
                                                index + +showUnsavedSearchItem;

                                            return (
                                                <Draggable
                                                    componentClasses={cx(styles.dragListItem, {
                                                        [styles.itemBorder]:
                                                            searchesCount - 1 !== currentIndex
                                                    })}
                                                    {...draggableProps}
                                                    iconPosition="center"
                                                    key={item.id}
                                                >
                                                    <SavedSearchItem
                                                        componentClasses={styles.listItem}
                                                        withShortcut={
                                                            withShortcut && item.index < 10
                                                        }
                                                        isFocused={focusedIndex === currentIndex}
                                                        item={item}
                                                        onDelete={onSearchRemove}
                                                        onToggleDefault={toggleDefault}
                                                        onExecute={onSelect}
                                                        onEdit={onEditSearch}
                                                        isActive={currentSearchId === item.id}
                                                    />
                                                </Draggable>
                                            );
                                        }}
                                    />
                                </Scrollbar>
                            </>
                        ) : null}

                        {recentSearches?.length ? (
                            <div style={{marginTop: 8, width: '100%'}}>
                                <div className={cx(styles.container, styles.title)}>
                                    <Title
                                        skin="h6"
                                        fontWeight="medium"
                                        text={texts.recentSearches}
                                    />
                                </div>

                                <Scrollbar
                                    rtl={isRTL()}
                                    autoHeightMax={180}
                                    autoHeight
                                    componentClasses={styles.scroll}
                                >
                                    {recentSearches.map((item, index) => (
                                        <SavedSearchItem
                                            componentClasses={styles.listItem}
                                            isFocused={false}
                                            item={item}
                                            key={item.name}
                                            itemBorder={index !== recentSearches.length - 1}
                                            onExecute={onSelect}
                                        />
                                    ))}
                                </Scrollbar>
                            </div>
                        ) : (
                            ''
                        )}

                        {openNewSearch && (
                            <div className={cx(styles.container, styles.footer)}>
                                <Button
                                    size="large"
                                    skin="text"
                                    prefixIcon={
                                        <Icon
                                            iconColor="primary-700"
                                            icon={PriorityIcon.newAdd}
                                            componentClasses={styles.roundIcon}
                                        />
                                    }
                                    onClick={openNewSearch}
                                >
                                    {texts.createSearch}
                                </Button>
                            </div>
                        )}
                    </Flex>
                </DropdownV2>
            </DropDownV2Context.Provider>
        );
    }
);
